import { useCallback, useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { Routes, Route, useParams, useNavigate, To } from 'react-router-dom'

import { TenantResult } from "interface/tenant"
import { GroupLoginResult, LoginResult } from "interface/login"

import { TenantProvider } from 'features/tenant/context'
import { UserSessionProvider } from 'features/session/context'
import { UserSession } from 'features/session/types'

import { Login } from 'features/login'
import { UserRegisterPage } from 'features/user/register'
import { InviteRequestPage } from 'features/user/invite/request'

import { InsideRoute } from 'routes/insideRoute'

const sessionKey = `session_${process.env.NODE_ENV}`

type LogoffProps = {
  onLogoff: (keepUrl: boolean) => void
}
const Logoff = ({
  onLogoff
}: LogoffProps) => {
  useEffect(
    () => {
      onLogoff(false)
    },
    [onLogoff]
  )
  return <></>
}

export const TenantRoot = () => {
  const navigate = useNavigate()
  const { domain } = useParams()
  const [, setCookie, removeCookie] = useCookies([sessionKey, 'notification'])

  const onLogin = useCallback(
    (
      tenant: TenantResult,
      loginResult: LoginResult,
      groupLoginResult: GroupLoginResult,
      from: To | undefined
    ) => {
      setCookie(
        sessionKey,
        encodeURIComponent(
          JSON.stringify({
            domain: tenant.domain,
            tenantId: tenant.tenantId,
            tenantName: tenant.tenantName,
            groupId: groupLoginResult.groupId,
            groupName: groupLoginResult.groupName,
            userId: loginResult.userId,
            userName: loginResult.userName,
            loginToken: loginResult.loginToken,
            searchId: null,
            roles: loginResult.roles,
            inside: loginResult.inside,
            hasGenAI: loginResult.hasGenAI
          } as UserSession)
        )
      )
      removeCookie('notification')
      if (from && !(typeof from === 'string' && from.endsWith('/login'))) {
        navigate(from, { relative: 'route' })
      } else {
        navigate(`/${domain}`, { relative: 'route' })
      }
    },
    [domain, setCookie, navigate, removeCookie]
  )
  const onLogoff = useCallback(
    (keepUrl: boolean) => {
      removeCookie(sessionKey)
      if (keepUrl) {
        const path = window.location.href.substring(window.location.origin.length)
        if (!path.endsWith('/login')) {
          navigate(`/${domain}/login`, { relative: 'route', state: { from: path } })
        }
      } else {
        navigate(`/${domain}/login`, { relative: 'route' })
      }
    },
    [removeCookie, domain, navigate]
  )
  if (!domain) {
    navigate(`/error/`)
    return <></>
  }
  return (
    <div className="tenantRoot">
      <TenantProvider domain={domain}>
        <Routes>
          <Route path={'login'} element={<Login onSubmit={onLogin} />} />
          <Route path={'logoff'} element={<Logoff onLogoff={onLogoff}/>} />
          <Route path={'register/:inviteId'} element={<UserRegisterPage />} />
          <Route path={'request'} element={<InviteRequestPage/>} />
          <Route path={'*'} element={
            <UserSessionProvider onLogout={() => onLogoff(true)}>
              <InsideRoute onLogoff={onLogoff} />
            </UserSessionProvider>
          } />
        </Routes>
      </TenantProvider>
    </div>
  )
}
